/// <amd-module name="Build/Designer/NewEditorHeader" />
import * as React from "react";

import { BuildDefinition, BuildTrigger, DefinitionQueueStatus, DefinitionTriggerType } from "Build/Client/Contracts/Build";
import { IBuildLinkingService } from "Build/Client/Linking";
import { BuildMessageInfo } from "Build/Common/Library/Designer.types";
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { EditorConstants, LegacyDesignerTabKeys } from "Build/Designer/Constants";
import * as Resources from "Build/Designer/Resources";

import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { getPipelineEditorViewService, IPipelineEditorViewService } from "Build/Designer/Services/PipelineEditorViewService";
import { IButtonProps } from "VSSUI/Button";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MenuItemType } from "VSSUI/Menu";
import { SplitButton } from "VSSUI/SplitButton";

export interface NewEditorHeaderProps {
    title: string;
    definition?: BuildDefinition;
    isYamlDirty: boolean;

    actionButtonLabel: string;
    actionDisabled?: boolean;
    onActionClick: () => void;
    onSaveWithoutValidationClick: () => void;

    onSettingsOpen: () => void;
    onVariablesOpen: () => void;
    onValidateYaml: () => void;
    onFullYaml: () => void;
    onRunWithParameters: () => void;

    buildMessageInfo?: BuildMessageInfo;
    onBuildMessageDismissed: () => void;

    errorMessage?: string;
    errorActions?: IButtonProps[];
    onErrorMessageDismissed?: () => void;
}

interface NewEditorHeaderState {}

export class NewEditorHeader extends VssComponent<NewEditorHeaderProps, NewEditorHeaderState, IVssComponentContext> {
    private viewService: IPipelineEditorViewService;
    private isTemplate: boolean;
    private isVariablesPanelEnabled: boolean;
    private isValidateAndSavePipelineByDefaultEnabled: boolean;

    constructor(props: NewEditorHeaderProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        this.viewService = getPipelineEditorViewService(context.pageContext);
        this.isTemplate = this.viewService.getIsTemplate();
        this.isVariablesPanelEnabled = !isFeatureFlagEnabled(context.pageContext, "Build2.DisableYamlEditorVariables", false);
        this.isValidateAndSavePipelineByDefaultEnabled = isFeatureFlagEnabled(context.pageContext, "Build2.ValidateAndSavePipelineByDefault", false);
    }

    public render(): JSX.Element {
        return (
            <>
                <Header
                    className="ci-editor-header"
                    title={this.getTitle()}
                    titleSize={TitleSize.Large}
                    backButtonProps={{ onClick: this.gotoPipelineResults }}
                    commandBarItems={this.getCommandBarItems()}
                ></Header>
                {this.getMessages()}
            </>
        );
    }

    /**
     * Get the pipeline title including a status tag if disabled, paused, or CI disabled
     */
    private getTitle(): string | JSX.Element {
        const queueStatus = (this.props.definition && this.props.definition.queueStatus) || DefinitionQueueStatus.Enabled;
        let status = "";

        if (queueStatus === DefinitionQueueStatus.Disabled) {
            status = Resources.PipelineStatusDisabled;
        } else if (queueStatus === DefinitionQueueStatus.Paused) {
            status = Resources.PipelineStatusPaused;
        } else if (this.props.definition && !this.isCIEnabled()) {
            status = Resources.PipelineStatusCIDisabled;
        }

        return (
            <>
                <span>{this.props.title}</span>
                {status && <span className="pipeline-status">{status}</span>}
            </>
        );
    }

    private getCommandBarItems(): IHeaderCommandBarItem[] {
        const commands: IHeaderCommandBarItem[] = [];

        // Templates should show the "Save" button and nothing else.
        if (this.isTemplate) {
            const save: IHeaderCommandBarItem = {
                id: "save",
                className: EditorConstants.ElementToFocusOnEscape,
                text: this.props.actionButtonLabel,
                important: true,
                disabled: this.props.actionDisabled,
                isPrimary: true,
                onActivate: this.props.onActionClick
            };
            commands.push(save);
            return commands;
        }

        if (this.isVariablesPanelEnabled) {
            const variablesPanel: IHeaderCommandBarItem = {
                id: "variablesPanel",
                text: Resources.CommandVariables,
                important: true,
                disabled: !this.props.definition,
                isPrimary: false,
                onActivate: this.props.onVariablesOpen
            };
            commands.push(variablesPanel);
        }

        var runSave: IHeaderCommandBarItem = {
            id: "runsave",
            className: EditorConstants.ElementToFocusOnEscape,
            text: this.props.actionButtonLabel,
            important: true,
            disabled: this.props.actionDisabled,
            isPrimary: true,
            onActivate: this.props.onActionClick
        };

        if (this.isValidateAndSavePipelineByDefaultEnabled && this.props.isYamlDirty) {
            runSave = {
                id: "runsave",
                important: true,
                disabled: this.props.actionDisabled,
                renderButton: () => {
                    return (
                        <SplitButton
                            primary
                            disabled = { this.props.actionDisabled }
                            key="runsave"
                            className={css("runsave-with-validation-button", EditorConstants.ElementToFocusOnEscape)}
                            buttonProps={{
                                text: this.props.actionButtonLabel,
                                onClick: this.props.onActionClick,
                                id: "runsave-with-validation-button",
                                role: "menuitem"
                            }}
                            menuButtonProps={{
                                subtle: true,
                                iconProps: { iconName: "ChevronDown" },
                                role: "menuitem",
                                contextualMenuProps: {
                                    menuProps: {
                                        id: `runsave-menu`,
                                        items: [
                                            {
                                                id: "runsave-without-validation-menu-item-button",
                                                onActivate: this.props.onSaveWithoutValidationClick,
                                                text: Resources.SaveWithoutValidationButtonLabel,
                                            }
                                        ]
                                    }
                                }
                            }}
                        />
                    );
                },
                onActivate: this.props.onActionClick
            };
        }
        commands.push(runSave);

        const settings: IHeaderCommandBarItem = {
            id: "settings",
            text: Resources.CommandSettings,
            important: false,
            disabled: !this.props.definition,
            iconProps: { iconName: "Settings" },
            onActivate: this.props.onSettingsOpen
        };
        commands.push(settings);

        const sep1: IHeaderCommandBarItem = {
            id: "sep1",
            itemType: MenuItemType.Divider,
            important: false
        };
        commands.push(sep1);

        const validate: IHeaderCommandBarItem = {
            id: "validate",
            text: Resources.Validate,
            important: false,
            disabled: !this.props.definition,
            iconProps: { iconName: "CheckMark" },
            onActivate: this.props.onValidateYaml
        };
        commands.push(validate);

        const fullYaml: IHeaderCommandBarItem = {
            id: "fullyaml",
            text: Resources.FullYaml,
            important: false,
            disabled: !this.props.definition,
            iconProps: { iconName: "FileYML" },
            onActivate: this.props.onFullYaml
        };
        commands.push(fullYaml);

        const sep2: IHeaderCommandBarItem = {
            id: "sep2",
            itemType: MenuItemType.Divider,
            important: false
        };
        commands.push(sep2);

        const triggers: IHeaderCommandBarItem = {
            id: "triggers",
            text: Resources.CommandTriggers,
            important: false,
            disabled: !this.props.definition,
            iconProps: { iconName: "LightningBolt" },
            onActivate: (menuItem, event) => this.gotoLegacyDesigner(LegacyDesignerTabKeys.Triggers, event)
        };
        commands.push(triggers);

        if (!this.isVariablesPanelEnabled) {
            const variables: IHeaderCommandBarItem = {
                id: "variables",
                text: Resources.CommandVariables,
                important: false,
                disabled: !this.props.definition,
                iconProps: { iconName: "Variable" },
                onActivate: (menuItem, event) => this.gotoLegacyDesigner(LegacyDesignerTabKeys.Variables, event)
            };
            commands.push(variables);
        }

        return commands;
    }

    private gotoPipelineResults = (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const definitionUrl = this.buildLinkingService.getDefinitionUrl(this.props.definition && this.props.definition.id);
        this.gotoUrl(definitionUrl, event);
    };

    private gotoLegacyDesigner = (viewName: string, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const definitionEditUrl = this.buildLinkingService.getDefinitionEditUrl(this.props.definition!.id, viewName);
        this.gotoUrl(definitionEditUrl, event);
    };

    private gotoUrl(url: string, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) {
        if (event && (event.ctrlKey || event.shiftKey)) {
            openLinkSecurely(url, true);
        } else {
            onClickFPS(this.context.pageContext, url, true);
        }
    }

    private getMessages(): JSX.Element | undefined {
        const errorMessageBar = this.getErrorBar();
        const buildMessageBar = this.getBuildMessageBar();
        if (errorMessageBar || buildMessageBar) {
            return (
                <div className="page-content flex-row flex-noshrink">
                    {errorMessageBar}
                    {buildMessageBar}
                </div>
            );
        }
    }

    private getErrorBar(): JSX.Element | undefined {
        if (this.props.errorMessage) {
            return (
                <MessageCard
                    className="flex-grow"
                    severity={MessageCardSeverity.Error}
                    buttonProps={this.props.errorActions}
                    onDismiss={this.props.onErrorMessageDismissed}
                >
                    {this.props.errorMessage}
                </MessageCard>
            );
        }
    }

    private getBuildMessageBar(): JSX.Element | undefined {
        if (this.props.buildMessageInfo) {
            const buildInfo = this.props.buildMessageInfo;
            const buildUrl = buildInfo.id > 0 ? this.buildLinkingService.getBuildUrl(buildInfo.id) : "";
            return (
                <MessageCard className="flex-grow" severity={MessageCardSeverity.Info} onDismiss={this.props.onBuildMessageDismissed}>
                    {buildInfo.isQueuing ? (
                        Resources.BuildQueuingMessage
                    ) : (
                        <FormatComponent format={Resources.BuildQueuedMessage}>
                            <FPSLink href={buildUrl}>{format(Resources.BuildNumberFormat, buildInfo.number)}</FPSLink>
                        </FormatComponent>
                    )}
                </MessageCard>
            );
        }
    }

    private isCIEnabled(): boolean {
        const triggers: BuildTrigger[] = this.props.definition!.triggers;
        const ciTrigger = triggers && triggers.find(t => t.triggerType === DefinitionTriggerType.ContinuousIntegration);
        return !!ciTrigger;
    }

    private get buildLinkingService(): IBuildLinkingService {
        return this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
    }
}
